import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

import { Colors } from '../../variables/colors';

type CircularProgressIndicatorColors = Colors.colorGreen | Colors.colorYellow;

@Component({
  selector: 'rt-circular-progress-indicator',
  templateUrl: './circular-progress-indicator.component.html',
  styleUrls: ['./circular-progress-indicator.component.less'],
})
export class CircularProgressIndicatorComponent implements OnChanges, OnInit {
  public color: CircularProgressIndicatorColors;
  public size: number = 18;
  public borderWidth: number = 1;
  public borderSpacing: number = 2;

  @ViewChild('svg', { static: true }) public svgRef: ElementRef;
  @ViewChild('circleContainer', { static: true }) public circleContainerRef: ElementRef;

  @Input() public percent: number = 0;

  constructor(private renderer: Renderer2) {}

  public get currentColor(): CircularProgressIndicatorColors {
    return this.color || this.getIndicatorColor();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      'percent' in changes &&
      (changes.percent.currentValue || changes.percent.currentValue === 0)
    ) {
      this.updateSvgPath();
      this.updateSvgColor();
      this.setBorderWidth();
    }
  }

  public ngOnInit(): void {
    this.generateChart();
    this.updateSvgHeight();
    this.setupContainerDimensions();
    this.updateSvgColor();
    this.setBorderWidth();
    this.setBorderSpacing();
  }

  public generateChart(): void {
    const pathEl = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    pathEl.setAttribute('d', this.generatePath());
    pathEl.setAttribute('fill', this.currentColor);

    this.renderer.appendChild(this.svgRef.nativeElement, pathEl);
  }

  private generatePath(): string {
    const [startX, startY] = this.getCoordinatesForPercent(0);
    const [endX, endY] = this.getCoordinatesForPercent(this.percent / 100);
    const largeArcFlag = this.percent / 100 > 0.5 ? 1 : 0;
    return [`M ${startX} ${startY}`, `A 1 1 0 ${largeArcFlag} 1 ${endX} ${endY}`, `L 0 0`].join(
      ' ',
    );
  }

  private getCoordinatesForPercent(percent: number): [number, number] {
    const x = Math.cos(2 * Math.PI * percent);
    const y = Math.sin(2 * Math.PI * percent);
    return [x, y];
  }

  private updateSvgHeight(): void {
    if (this.svgRef && this.size) {
      this.renderer.setStyle(this.svgRef.nativeElement, 'height', `${this.size}px`);
    }
  }

  private updateSvgColor(): void {
    if (this.svgRef.nativeElement.querySelector('path')) {
      this.svgRef.nativeElement.querySelector('path').setAttribute('fill', this.currentColor);
    }
  }

  private updateSvgPath(): void {
    if (this.svgRef.nativeElement.querySelector('path')) {
      this.svgRef.nativeElement.querySelector('path').setAttribute('d', this.generatePath());
    }
  }

  private setupContainerDimensions(): void {
    if (this.circleContainerRef && this.size) {
      this.renderer.setStyle(this.circleContainerRef.nativeElement, 'height', `${this.size}px`);
      this.renderer.setStyle(this.circleContainerRef.nativeElement, 'width', `${this.size}px`);
    }
  }

  private setBorderWidth(): void {
    if (this.circleContainerRef && this.borderWidth) {
      this.renderer.setStyle(
        this.circleContainerRef.nativeElement,
        'border',
        `${this.borderWidth}px solid ${this.currentColor}`,
      );
    }
  }

  private setBorderSpacing(): void {
    if (this.circleContainerRef && this.borderSpacing) {
      this.renderer.setStyle(
        this.circleContainerRef.nativeElement,
        'padding',
        `${this.borderSpacing}px`,
      );
    }
  }

  private getIndicatorColor(): CircularProgressIndicatorColors {
    return this.percent === 100 ? Colors.colorGreen : Colors.colorYellow;
  }
}
